import { z } from 'zod';
import type { DeviceInstanceId, VmId } from '../domain/ids';
import type { DatabaseInstance, DbInstanceWritePayload } from '../domain/databaseInstance';
import {
  softwareAssociationRepository,
  type SoftwareAssociationDto,
} from './softwareAssociation.repository';

// T39 (fusionada con T40, ver softwareAssociation.repository.ts para el detalle del contrato
// unificado): v1 no tiene `/database-instances` — las instancias de BD son
// `software-associations` con `category: 'database_instance'` bajo
// `/device-instances/{deviceInstanceId}/virtual-machines/{vmId}/software-associations`.
// `deviceInstanceId` se añade como PRIMER parámetro a `getAll`/`create`/`update`/`remove` (antes no
// lo recibía este repositorio) — propagado desde `useVmDatabaseInstances.ts`, que a su vez lo
// recibe de `VmDatabasesTab.tsx` (nuevo prop) y de `VmDetailPage.tsx` (ya lo tenía como
// `instanceId`).
//
// `databaseSystemId` (dominio) ahora es `string` (antes `number`): mapea 1:1 a `catalogSelectionId`
// de v1, que es siempre string (igual que `CatalogOption.id` en `catalogOptions.repository.ts`).
//
// GAP DE DATOS no bloqueante: `dbSystemName` (dominio) solo tiene como fuente
// `context.catalogSelectionLabel` (v1) — un único string, no el objeto de catálogo completo que
// enriquecía el legado. El resto de campos (`instanceName`/`port`/`environment`/`charset`/
// `storageSizeGb`/`isClustered`/`backupEnabled`/`monitoringEnabled`/`version`) viven en
// `attributes` (bolsa libre), con el mismo nombre de campo (snake_case) que el legado ya usaba —
// se valida con zod al leer, igual que cualquier otro DTO de `infrastructure/`.
// `dbEndOfSupport`/`dbEndOfLife` (solo lectura, ausentes del payload de escritura tanto en legado
// como aquí) también viven en `attributes`.
//
// `update` no tiene endpoint de detalle en v1 (solo listado) — se relee `getAll` filtrado por
// `category` y se busca por `id` para obtener el `version` de concurrencia optimista que exige
// `PATCH .../software-associations/{id}` (mismo principio de "releer antes de actualizar" que
// T31/T38, adaptado a la ausencia de GET individual).

/** `DatabaseInstance.id` es `string | number` (domain/databaseInstance.ts) — el repositorio
 * coerciona a `string` al hablar con `softwareAssociationRepository` (el id de asociación de v1 es
 * siempre string), preservando la firma pública existente para no romper `application/`. */
type DatabaseInstanceId = string | number;

const DatabaseAttributesSchema = z.object({
  instance_name: z.string(),
  port: z.union([z.number(), z.string()]),
  environment: z.string().nullish(),
  charset: z.string().nullish(),
  storage_size_gb: z.number().nullish(),
  is_clustered: z.boolean().nullish(),
  backup_enabled: z.boolean().nullish(),
  monitoring_enabled: z.boolean().nullish(),
  version: z.string().nullish(),
  db_end_of_support: z.string().nullish(),
  db_end_of_life: z.string().nullish(),
});

export interface DatabaseInstanceListParams {
  vmId?: VmId;
}

function toDomain(dto: SoftwareAssociationDto): DatabaseInstance {
  const a = DatabaseAttributesSchema.parse(dto.attributes);
  return {
    id: dto.id,
    vmId: dto.virtual_machine_id as VmId,
    databaseSystemId: dto.catalog_selection_id,
    dbSystemName: dto.context?.catalog_selection_label ?? undefined,
    version: a.version ?? undefined,
    instanceName: a.instance_name,
    port: a.port,
    environment: a.environment ?? undefined,
    charset: a.charset ?? undefined,
    storageSizeGb: a.storage_size_gb ?? null,
    isClustered: a.is_clustered ?? undefined,
    backupEnabled: a.backup_enabled ?? undefined,
    monitoringEnabled: a.monitoring_enabled ?? undefined,
    dbEndOfSupport: a.db_end_of_support ?? undefined,
    dbEndOfLife: a.db_end_of_life ?? undefined,
  };
}

function toAttributes(payload: DbInstanceWritePayload): Record<string, unknown> {
  return {
    instanceName: payload.instanceName,
    port: payload.port,
    environment: payload.environment,
    charset: payload.charset,
    storageSizeGb: payload.storageSizeGb,
    version: payload.version,
    isClustered: payload.isClustered,
    backupEnabled: payload.backupEnabled,
    monitoringEnabled: payload.monitoringEnabled,
  };
}

export const databaseInstanceRepository = {
  async getAll(
    deviceInstanceId: DeviceInstanceId,
    params?: DatabaseInstanceListParams
  ): Promise<DatabaseInstance[]> {
    if (params?.vmId === undefined) return [];
    const all = await softwareAssociationRepository.getAll(
      deviceInstanceId,
      params.vmId,
      'database_instance'
    );
    return all.map(toDomain);
  },

  async create(
    deviceInstanceId: DeviceInstanceId,
    vmId: VmId,
    payload: DbInstanceWritePayload
  ): Promise<DatabaseInstance> {
    const dto = await softwareAssociationRepository.create(deviceInstanceId, vmId, {
      category: 'database_instance',
      catalogSelectionId: payload.databaseSystemId,
      attributes: toAttributes(payload),
    });
    return toDomain(dto);
  },

  async update(
    deviceInstanceId: DeviceInstanceId,
    vmId: VmId,
    id: DatabaseInstanceId,
    payload: DbInstanceWritePayload
  ): Promise<DatabaseInstance> {
    const associationId = String(id);
    const current = await softwareAssociationRepository.getAll(
      deviceInstanceId,
      vmId,
      'database_instance'
    );
    const existing = current.find((a) => a.id === associationId);
    const dto = await softwareAssociationRepository.update(deviceInstanceId, vmId, associationId, {
      version: existing?.version ?? 1,
      attributes: toAttributes(payload),
    });
    return toDomain(dto);
  },

  async remove(
    deviceInstanceId: DeviceInstanceId,
    vmId: VmId,
    id: DatabaseInstanceId
  ): Promise<void> {
    await softwareAssociationRepository.remove(deviceInstanceId, vmId, String(id));
  },
};
